
import {
  Box,
  Button,
  Grid,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";

      import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RefreshIcon from "@mui/icons-material/Refresh";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import StorageIcon from "@mui/icons-material/Storage";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import DataUsageIcon from "@mui/icons-material/DataUsage";

import { useEffect, useMemo, useState } from "react";

import BreadcrumbsBar from "../../../component/common/BreadcrumbsBar";

import {
  currentMonth,
  getDashboardDataStatus,
  dataFinalised,
} from "../../../services/dashBoardService";

import { monthNames } from "../../../component/constent";

import SEO from "../../../utils/SEO";
import PageHeader from "../../../utils/PageHeader";


/* =========================================================
   REQUIRED INPUT DATA
   NOTE:
   labour_sub_ledger is NOT included here because it is
   the OUTPUT of processing.
========================================================= */

const dataItems = [
  {
    key: "inputdata_immis",
    title: "Store Data",
    subtitle: "iMMIS",
    icon: StorageIcon,
  },
  {
    key: "labour_timeoffice",
    title: "Time Office Data",
    subtitle: "Labour",
    icon: CalendarMonthIcon,
  },
  {
    key: "labour_insentive",
    title: "Incentive Data",
    subtitle: "Labour",
    icon: TrendingUpIcon,
  },
  {
    key: "inputdata_ioh",
    title: "IOH Data",
    subtitle: "Input Data",
    icon: DataUsageIcon,
  },
  {
    key: "inputdata_poh",
    title: "POH Data",
    subtitle: "Input Data",
    icon: DataUsageIcon,
  },
  {
    key: "inputdata_wls",
    title: "WLS Data",
    subtitle: "Input Data",
    icon: StorageIcon,
  },
  {
    key: "enga_section_data",
    title: "EGA / ENGA Data",
    subtitle: "Section Data",
    icon: DataUsageIcon,
  },
  {
    key: "inputdata_creditjv",
    title: "Credit JV Data",
    subtitle: "Input Data",
    icon: StorageIcon,
  },
  {
    key: "freightandproformaoncost",
    title: "Store Proforma",
    subtitle: "Freight & Proforma",
    icon: StorageIcon,
  },
];


export default function DataFinalised() {

  const [monthData, setMonthData] = useState(null);
  const [status, setStatus] = useState(null);

  const [loadingMonth, setLoadingMonth] = useState(true);
  const [loadingStatus, setLoadingStatus] = useState(false);

  const [processing, setProcessing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [error, setError] = useState("");


  /* =========================================================
     LOAD CURRENT MONTH
  ========================================================= */

  const loadCurrentMonth = async () => {

    try {

      setLoadingMonth(true);
      setError("");

      const res = await currentMonth();

      setMonthData(res?.data);

    } catch (err) {

      console.error("Current month error:", err);

      setError(
        err?.response?.data?.message ||
        "Unable to load current month."
      );

    } finally {

      setLoadingMonth(false);

    }
  };


  /* =========================================================
     LOAD DATA STATUS
  ========================================================= */

  const loadStatus = async () => {

    try {

      const month = monthData?.data?.month;
      const year = monthData?.data?.year;

      if (!month || !year) {
        return;
      }

      setLoadingStatus(true);
      setError("");

      const res =
        await getDashboardDataStatus(
          month,
          year
        );

      /*
       Backend response:

       {
         success: true,
         data: {
           inputdata_immis: {
             status: true,
             rowCount: 123
           }
         }
       }
      */

      setStatus(res?.data);

    } catch (err) {

      console.error(
        "Data status error:",
        err
      );

      setError(
        err?.response?.data?.message ||
        "Unable to load data status."
      );

    } finally {

      setLoadingStatus(false);

    }
  };


  /* =========================================================
     INITIAL LOAD
  ========================================================= */

  useEffect(() => {

    loadCurrentMonth();

  }, []);


  /* =========================================================
     LOAD STATUS AFTER MONTH
  ========================================================= */

  useEffect(() => {

    if (
      monthData?.data?.month &&
      monthData?.data?.year
    ) {

      loadStatus();

    }

  }, [monthData]);


  /* =========================================================
     STATUS DATA
  ========================================================= */

  const statusData =
    status?.data || {};


  /* =========================================================
     CHECK INPUT STATUS
  ========================================================= */

  const isAvailable = (key) => {

    const item =
      statusData?.[key];

    return Boolean(
      item?.status
    );
  };


  /* =========================================================
     GET ROW COUNT
  ========================================================= */

  const getRowCount = (key) => {

    return Number(
      statusData?.[key]?.rowCount || 0
    );
  };


  /* =========================================================
     SUMMARY
  ========================================================= */

  const summary = useMemo(() => {

    let completed = 0;
    let pending = 0;
    let totalRows = 0;

    dataItems.forEach((item) => {

      const data =
        statusData?.[item.key];

      const available =
        Boolean(data?.status);

      const rows =
        Number(data?.rowCount || 0);

      totalRows += rows;

      if (available) {
        completed++;
      } else {
        pending++;
      }

    });


    const total =
      dataItems.length;


    const percentage =
      total > 0
        ? Math.round(
            (completed / total) * 100
          )
        : 0;


    return {
      total,
      completed,
      pending,
      totalRows,
      percentage,
    };

  }, [statusData]);


  /* =========================================================
     MISSING DATA
  ========================================================= */

  const missingData = dataItems.filter(
    (item) =>
      !isAvailable(item.key)
  );


  const allDataAvailable =
    missingData.length === 0;


  /* =========================================================
     PROCESSING RESULT
  ========================================================= */

  const processingData =
    statusData?.labour_sub_ledger;


  const processingAvailable =
    Boolean(
      processingData?.status
    );


  const processingRows =
    Number(
      processingData?.rowCount || 0
    );


  /* =========================================================
     CURRENT PERIOD
  ========================================================= */

  const currentMonthNumber =
    monthData?.data?.month;

  const currentYear =
    monthData?.data?.year;


  const currentMonthName =
    currentMonthNumber
      ? monthNames[
          Number(currentMonthNumber) 
        ]
      : "";


  /* =========================================================
     REFRESH
  ========================================================= */

  const handleRefresh = async () => {

    try {

      setRefreshing(true);

      await loadCurrentMonth();

    } finally {

      setRefreshing(false);

    }
  };


  /* =========================================================
     FINALISE / PROCESS
  ========================================================= */

  const handleProcess = async () => {

    if (!allDataAvailable) {

      return;

    }


    const month =
      monthData?.data?.month;

    const year =
      monthData?.data?.year;


    if (!month || !year) {

      alert(
        "Processing month and year are not available."
      );

      return;

    }


    const confirmed =
      window.confirm(
        `All required data is available for ${currentMonthName} ${year}.\n\n` +
        `Total input rows: ${summary.totalRows.toLocaleString()}\n\n` +
        `Are you sure you want to Finalise and Process this month's data?`
      );


    if (!confirmed) {

      return;

    }


    try {

      setProcessing(true);
      setError("");


      const res =
        await dataFinalised(
          month,
          year
        );


      console.log(
        "Data finalisation response:",
        res
      );


      alert(
        "Data finalised and processed successfully."
      );


      /* Refresh after processing */

      await loadStatus();

    } catch (err) {

      console.error(
        "Data finalisation error:",
        err
      );


      const message =
        err?.response?.data?.message ||
        "Data finalisation failed. Please try again.";


      alert(message);


      setError(message);

    } finally {

      setProcessing(false);

    }
  };


  /* =========================================================
     DATA STATUS CARD
  ========================================================= */

  const StatusCard = ({ item }) => {

    const Icon =
      item.icon || StorageIcon;


    const available =
      isAvailable(item.key);


    const rowCount =
      getRowCount(item.key);


    return (

      <Paper
        elevation={0}
        sx={{
          height: "100%",
          minHeight: 155,

          borderRadius: 3,

          overflow: "hidden",

          border:
            available
              ? "1px solid #b7dfb9"
              : "1px solid #ffcdd2",

          background:
            available
              ? "linear-gradient(145deg,#f0faf1,#ffffff)"
              : "linear-gradient(145deg,#fff5f5,#ffffff)",

          transition:
            "all 0.25s ease",

          "&:hover": {
            transform:
              "translateY(-4px)",

            boxShadow:
              "0 10px 25px rgba(0,0,0,0.10)",
          },
        }}
      >

        {/* STATUS BAR */}

        <Box
          sx={{
            height: 5,

            backgroundColor:
              available
                ? "#2e7d32"
                : "#d32f2f",
          }}
        />


        <Box
          sx={{
            p: 2,
          }}
        >

          {/* HEADER */}

          <Box
            sx={{
              display: "flex",
              justifyContent:
                "space-between",
              alignItems: "flex-start",
              gap: 1,
            }}
          >

            <Box
              sx={{
                display: "flex",
                gap: 1.2,
                alignItems: "center",
              }}
            >

              <Box
                sx={{
                  width: 42,
                  height: 42,

                  borderRadius: 2,

                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",

                  backgroundColor:
                    available
                      ? "#c8e6c9"
                      : "#ffcdd2",

                  color:
                    available
                      ? "#2e7d32"
                      : "#c62828",
                }}
              >

                <Icon
                  fontSize="small"
                />

              </Box>


              <Box>

                <Typography
                  sx={{
                    fontWeight: 750,
                    fontSize:
                      "0.94rem",
                    lineHeight: 1.2,
                  }}
                >
                  {item.title}
                </Typography>


                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  {item.subtitle}
                </Typography>

              </Box>

            </Box>


            {available ? (

              <CheckCircleIcon
                sx={{
                  color: "#2e7d32",
                  fontSize: 25,
                }}
              />

            ) : (

              <CancelIcon
                sx={{
                  color: "#d32f2f",
                  fontSize: 25,
                }}
              />

            )}

          </Box>


          {/* ROW COUNT */}

          <Box
            sx={{
              mt: 2,
              display: "flex",
              alignItems: "baseline",
              gap: 0.7,
            }}
          >

            <Typography
              sx={{
                fontSize:
                  "1.55rem",
                fontWeight: 800,
                lineHeight: 1,
              }}
            >
              {rowCount.toLocaleString()}
            </Typography>


            <Typography
              variant="body2"
              color="text.secondary"
            >
              rows
            </Typography>

          </Box>


          {/* STATUS */}

          <Chip
            size="small"
            icon={
              available
                ? <CheckCircleIcon />
                : <CancelIcon />
            }
            label={
              available
                ? "Data Available"
                : "Data Missing"
            }
            sx={{
              mt: 1.3,

              fontWeight: 700,

              backgroundColor:
                available
                  ? "#e8f5e9"
                  : "#ffebee",

              color:
                available
                  ? "#2e7d32"
                  : "#c62828",
            }}
          />

        </Box>

      </Paper>
    );
  };


  /* =========================================================
     RENDER
  ========================================================= */

  return (

    <>

      <SEO
        title="Data Finalisation - WGR Data Management System"
        description="Final validation and data processing."
        url="https://wgr.mwsgkp.in/data-finalised"
      />


      <BreadcrumbsBar
        page="Data Finalised"
      />


      <Box
        sx={{
          p: {
            xs: 1,
            sm: 2,
            md: 3,
          },
        }}
      >

        {/* =================================================
            HEADER
        ================================================= */}

         <PageHeader
        title=" Data Finalisation"
        subtitle={`Data entry status for the month of: ${currentMonthName} - ${currentYear}`}
        icon={AccessTimeIcon}
       // statusLabel={'Records: ' + totalRows}
       //  statusValue="CSV File"
        // statusChip={{
        //   label: loading
        //     ? "Loading..."
        //     : totalRows > 0
        //       ? "Data Found"
        //       : "No Data",
        //   color: loading ? "warning" : totalRows > 0 ? "primary" : "default",
        // }}
        showRefresh
        onRefresh={handleRefresh}
        // refreshing={loading}
      />






        {/* =================================================
            ERROR
        ================================================= */}

        {error && (

          <Alert
            severity="error"
            sx={{
              mb: 2,
              borderRadius: 2,
            }}
          >
            {error}
          </Alert>

        )}


        {/* =================================================
            LOADING
        ================================================= */}

        {loadingMonth ||
        loadingStatus ? (

          <Paper
            elevation={0}
            sx={{
              minHeight: 350,

              display: "flex",
              flexDirection: "column",

              alignItems: "center",
              justifyContent: "center",

              gap: 2,

              borderRadius: 3,

              border:
                "1px solid #e0e0e0",
            }}
          >

            <CircularProgress />

            <Typography
              color="text.secondary"
            >
              Checking monthly data status...
            </Typography>

          </Paper>

        ) : (

          <>

            {/* =============================================
                SUMMARY
            ============================================= */}

            <Grid
              container
              spacing={2}
              sx={{
                mb: 2.5,
              }}
            >

              {/* TOTAL */}

              <Grid
                size={{
                  xs: 6,
                  sm: 3,
                }}
              >

                <Paper
                  elevation={0}
                  sx={{
                    p: 2,

                    height: "100%",

                    borderRadius: 3,

                    border:
                      "1px solid #e0e0e0",
                  }}
                >

                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Required Sources
                  </Typography>


                  <Typography
                    variant="h4"
                    sx={{
                      mt: 0.5,
                      fontWeight: 800,
                    }}
                  >
                    {summary.total}
                  </Typography>

                </Paper>

              </Grid>


              {/* COMPLETED */}

              <Grid
                size={{
                  xs: 6,
                  sm: 3,
                }}
              >

                <Paper
                  elevation={0}
                  sx={{
                    p: 2,

                    height: "100%",

                    borderRadius: 3,

                    border:
                      "1px solid #c8e6c9",

                    background:
                      "#f1f8f3",
                  }}
                >

                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Available
                  </Typography>


                  <Typography
                    variant="h4"
                    sx={{
                      mt: 0.5,
                      fontWeight: 800,
                      color: "#2e7d32",
                    }}
                  >
                    {summary.completed}
                  </Typography>

                </Paper>

              </Grid>


              {/* PENDING */}

              <Grid
                size={{
                  xs: 6,
                  sm: 3,
                }}
              >

                <Paper
                  elevation={0}
                  sx={{
                    p: 2,

                    height: "100%",

                    borderRadius: 3,

                    border:
                      "1px solid #ffcdd2",

                    background:
                      "#fff5f5",
                  }}
                >

                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Missing
                  </Typography>


                  <Typography
                    variant="h4"
                    sx={{
                      mt: 0.5,
                      fontWeight: 800,
                      color: "#c62828",
                    }}
                  >
                    {summary.pending}
                  </Typography>

                </Paper>

              </Grid>


              {/* ROWS */}

              <Grid
                size={{
                  xs: 6,
                  sm: 3,
                }}
              >

                <Paper
                  elevation={0}
                  sx={{
                    p: 2,

                    height: "100%",

                    borderRadius: 3,

                    border:
                      "1px solid #bbdefb",

                    background:
                      "#f3f9ff",
                  }}
                >

                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Total Input Rows
                  </Typography>


                  <Typography
                    variant="h4"
                    sx={{
                      mt: 0.5,
                      fontWeight: 800,
                      color: "#1565c0",
                      fontSize: {
                        xs: "1.65rem",
                        sm: "2.1rem",
                      },
                    }}
                  >
                    {summary.totalRows.toLocaleString()}
                  </Typography>

                </Paper>

              </Grid>

            </Grid>


            {/* =============================================
                PROGRESS
            ============================================= */}

            <Paper
              elevation={0}
              sx={{
                p: 2.5,

                mb: 2.5,

                borderRadius: 3,

                border:
                  "1px solid #e0e0e0",
              }}
            >

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent:
                    "space-between",

                  mb: 1,
                }}
              >

                <Box>

                  <Typography
                    sx={{
                      fontWeight: 750,
                    }}
                  >
                    Data Readiness
                  </Typography>


                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    {summary.completed} of{" "}
                    {summary.total} required
                    sources available
                  </Typography>

                </Box>


                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: "1.2rem",
                    color:
                      allDataAvailable
                        ? "#2e7d32"
                        : "#1976d2",
                  }}
                >
                  {summary.percentage}%
                </Typography>

              </Box>


              <Box
                sx={{
                  height: 12,

                  borderRadius: 10,

                  backgroundColor:
                    "#e3f2fd",

                  overflow: "hidden",
                }}
              >

                <Box
                  sx={{
                    width:
                      `${summary.percentage}%`,

                    height: "100%",

                    borderRadius: 10,

                    background:
                      allDataAvailable
                        ? "linear-gradient(90deg,#2e7d32,#66bb6a)"
                        : "linear-gradient(90deg,#1976d2,#42a5f5)",

                    transition:
                      "width 0.5s ease",
                  }}
                />

              </Box>

            </Paper>


            {/* =============================================
                MAIN STATUS
            ============================================= */}

            <Alert
              severity={
                allDataAvailable
                  ? "success"
                  : "warning"
              }
              sx={{
                mb: 2.5,
                borderRadius: 2,
                fontWeight: 600,
              }}
            >

              {allDataAvailable

                ? `All ${summary.total} required data sources are available. The month is ready for finalisation and processing.`

                : `${summary.pending} required data source(s) are still missing. Finalisation is currently disabled.`}

            </Alert>


            {/* =============================================
                DATA CARDS
            ============================================= */}

            <Paper
              elevation={0}
              sx={{
                p: {
                  xs: 1.5,
                  sm: 2.5,
                },

                borderRadius: 3,

                border:
                  "1px solid #dfe3e8",

                background:
                  "#fafcff",
              }}
            >

              <Typography
                sx={{
                  mb: 2,

                  fontWeight: 800,

                  color: "#1565c0",

                  fontSize: {
                    xs: "1rem",
                    sm: "1.1rem",
                  },
                }}
              >
                Required Data Status
                {" • "}
                {currentMonthName}{" "}
                {currentYear}
              </Typography>


              <Grid
                container
                spacing={2}
              >

                {dataItems.map(
                  (item) => (

                    <Grid
                      key={item.key}
                      size={{
                        xs: 6,
                        sm: 6,
                        md: 4,
                        lg: 3,
                      }}
                    >

                      <StatusCard
                        item={item}
                      />

                    </Grid>

                  )
                )}

              </Grid>

            </Paper>


            {/* =============================================
                MISSING DATA
            ============================================= */}

            {!allDataAvailable && (

              <Paper
                elevation={0}
                sx={{
                  mt: 2.5,

                  p: 2.5,

                  borderRadius: 3,

                  border:
                    "1px solid #ffcc80",

                  background:
                    "#fffaf2",
                }}
              >

                <Typography
                  sx={{
                    fontWeight: 800,
                    color: "#e65100",
                    mb: 1,
                  }}
                >
                  Complete the following
                  before finalisation
                </Typography>


                <Grid
                  container
                  spacing={1}
                >

                  {missingData.map(
                    (item) => (

                      <Grid
                        key={item.key}
                        size={{
                          xs: 12,
                          sm: 6,
                          md: 4,
                        }}
                      >

                        <Box
                          sx={{
                            display: "flex",
                            alignItems:
                              "center",
                            gap: 1,

                            p: 1,

                            borderRadius: 2,

                            background:
                              "#fff3e0",
                          }}
                        >

                          <CancelIcon
                            sx={{
                              color:
                                "#d32f2f",
                              fontSize: 20,
                            }}
                          />

                          <Typography
                            variant="body2"
                            fontWeight={600}
                          >
                            {item.title}
                          </Typography>

                        </Box>

                      </Grid>

                    )
                  )}

                </Grid>

              </Paper>

            )}


            {/* =============================================
                FINALISE BUTTON
            ============================================= */}

            <Box
              sx={{
                mt: 3,

                display: "flex",
                justifyContent: "center",
              }}
            >

              <Button
                variant="contained"
                size="large"

                startIcon={
                  processing ? (

                    <CircularProgress
                      size={20}
                      color="inherit"
                    />

                  ) : (

                    <PlayArrowIcon />

                  )
                }

                disabled={
                  !allDataAvailable ||
                  processing
                }

                onClick={
                  handleProcess
                }

                sx={{
                  minWidth: {
                    xs: "100%",
                    sm: 300,
                  },

                  py: 1.5,

                  borderRadius: 2.5,

                  fontWeight: 800,

                  fontSize:
                    "1rem",

                  boxShadow:
                    allDataAvailable
                      ? "0 6px 18px rgba(25,118,210,0.25)"
                      : "none",
                }}
              >

                {processing
                  ? "Finalising & Processing..."
                  : "Finalise & Process Data"}

              </Button>

            </Box>


            {/* =============================================
                PROCESSING RESULT
            ============================================= */}

            <Paper
              elevation={0}
              sx={{
                mt: 3,

                borderRadius: 3,

                overflow: "hidden",

                border:
                  processingAvailable
                    ? "1px solid #a5d6a7"
                    : "1px solid #e0e0e0",

                background:
                  processingAvailable
                    ? "linear-gradient(135deg,#e8f5e9,#ffffff)"
                    : "#fafafa",
              }}
            >

              <Box
                sx={{
                  p: {
                    xs: 2,
                    sm: 2.5,
                  },

                  display: "flex",

                  alignItems: {
                    xs: "flex-start",
                    sm: "center",
                  },

                  justifyContent:
                    "space-between",

                  flexDirection: {
                    xs: "column",
                    sm: "row",
                  },

                  gap: 2,
                }}
              >

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >

                  <Box
                    sx={{
                      width: 48,
                      height: 48,

                      borderRadius: 2,

                      display: "flex",
                      alignItems:
                        "center",
                      justifyContent:
                        "center",

                      backgroundColor:
                        processingAvailable
                          ? "#c8e6c9"
                          : "#eeeeee",

                      color:
                        processingAvailable
                          ? "#2e7d32"
                          : "#757575",
                    }}
                  >

                    <PlayArrowIcon />

                  </Box>


                  <Box>

                    <Typography
                      sx={{
                        fontWeight: 800,
                      }}
                    >
                      Data Processing Status
                    </Typography>


                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      Labour Sub Ledger
                    </Typography>

                  </Box>

                </Box>


                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >

                  <Box
                    sx={{
                      textAlign: {
                        xs: "left",
                        sm: "right",
                      },
                    }}
                  >

                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 800,
                      }}
                    >
                      {processingRows.toLocaleString()}
                    </Typography>


                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      processed rows
                    </Typography>

                  </Box>


                  <Chip
                    icon={
                      processingAvailable
                        ? <CheckCircleIcon />
                        : <CancelIcon />
                    }
                    label={
                      processingAvailable
                        ? "Processed"
                        : "Not Processed"
                    }
                    sx={{
                      fontWeight: 700,

                      backgroundColor:
                        processingAvailable
                          ? "#c8e6c9"
                          : "#eeeeee",

                      color:
                        processingAvailable
                          ? "#2e7d32"
                          : "#757575",
                    }}
                  />

                </Box>

              </Box>

            </Paper>

          </>

        )}

      </Box>

    </>
  );
}

